import {Box, type BoxProps, merge} from '@primer/react'
import type {PropsWithChildren} from 'react'
import type {BetterSystemStyleObject} from '@primer/react/lib-esm/sx'
import {testIdProps} from '@github-ui/test-id-props'

export type MetadataProps = PropsWithChildren<{
  /**
   * Controls how the metadata will be aligned inside the trailing content. Defaults to 'left'.
   */
  alignment?: 'left' | 'right'
  /**
   * Alter the appearance of the metadata to appear more subtle than other ListItem content ('secondary', default) or
   * comparable to other ListItem content ('primary'). Affects text color, font size, width, and distance from other
   * metadata items.
   */
  variant?: 'primary' | 'secondary'
}> &
  BoxProps

// by default merge (via deepmerge) combines arrays by concatenating them
// this creates unexpected behavior when merging sx props (such as display)
// this util allows us more fine-grained control over how arrays are merged
const preferIncomingArray = (destinationArray: unknown[], sourceArray: unknown[]) =>
  sourceArray.length > 0 ? sourceArray : destinationArray

const commonSx: BetterSystemStyleObject = {display: ['none', 'flex'], flexDirection: 'row', alignItems: 'center'}
const secondaryVariantSx: BetterSystemStyleObject = {
  color: 'fg.muted',
  fontSize: 0,
  fontWeight: 'normal',
  flexShrink: 0,
  gap: 1,
}
const primaryVariantSx: BetterSystemStyleObject = {display: 'flex', pr: 3, gap: 3}

export function Metadata({children, alignment, variant, sx: externalSx, ...props}: MetadataProps) {
  const variantSx = variant === 'primary' ? primaryVariantSx : secondaryVariantSx
  const commonAndVariantSx = merge<BetterSystemStyleObject>(commonSx, variantSx)
  const defaultSx = merge<BetterSystemStyleObject>(commonAndVariantSx, {
    justifyContent: alignment === 'right' ? 'flex-end' : 'flex-start',
  })
  const sx = externalSx
    ? merge<BetterSystemStyleObject>(defaultSx, externalSx, {arrayMerge: preferIncomingArray})
    : defaultSx

  return (
    <Box sx={sx} {...testIdProps('listview-item-metadata-item')} {...props}>
      {children}
    </Box>
  )
}

Metadata.displayName = 'ListItem.Metadata'
